import { useEffect, useState } from "react";
import EditProfileModal from "@/components/EditProfileModal/EditProfileModal";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import Loader from "@/components/Loader/Loader";
import {
  ChevronRightMiniIcon,
  GlobeMiniIcon,
  HeadsetMiniIcon,
  NotificationMiniIcon,
  PencilMiniIcon,
  WalletMiniIcon,
} from "@/components/SVGComponents/MemberIcons";
import { useLanguage } from "@/contexts/LanguageContext";
import { AUTH_ROUTE_PATHS } from "@/pages/Auth/authConfig";
import type { ProfileOutletContext } from "@/pages/Profile/ProfileLayout/ProfileLayout";
import { useAppDispatch } from "@/store/hooks";
import { clear } from "@/store/slices/globalSlice";
import { fetchUserProfile } from "@/store/slices/profileSlice";
import toast from "@/lib/toast";
import {
  addBaseUrl,
  captalizeEachWord,
  clearOnLogout,
  getCurrentUser,
} from "@/lib/utils";
import { PROFILE_CHILD_PATHS } from "@/pages/Profile/profilePaths";
import LogoutConfirmModal, {
  type ConfirmActionVariant,
} from "@/components/LogoutConfirmModal/LogoutConfirmModal";
import { deleteUserAccount, updateUserProfile } from "@/services/api/userProfile";
import styles from "./Profile.module.scss";

const PROFILE_LINKS = [
  // {
  //   labelKey: "profile.links.language",
  //   fallbackLabel: "Language",
  //   to: PROFILE_CHILD_PATHS.LANGUAGE,
  //   icon: GlobeMiniIcon,
  // },
  {
    labelKey: "profile.links.helpCentre",
    fallbackLabel: "Help centre & FAQ",
    to: PROFILE_CHILD_PATHS.HELP_CENTRE,
    icon: HeadsetMiniIcon,
  },
  {
    labelKey: "profile.links.subscription",
    fallbackLabel: "My subscription",
    to: PROFILE_CHILD_PATHS.SUBSCRIPTION,
    icon: WalletMiniIcon,
  },
] as const;

const Profile = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { profile, isLoading, error } =
    useOutletContext<ProfileOutletContext>();
  const currentUser = getCurrentUser() || {};
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    profile?.userSettings?.isPushNotificationEnabled ??
      currentUser?.userSettings?.isPushNotificationEnabled ??
      true,
  );
  const [isUpdatingNotification, setIsUpdatingNotification] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState<ConfirmActionVariant | null>(
    null,
  );
  const fallbackName = captalizeEachWord(
    currentUser?.userName ||
      currentUser?.name ||
      currentUser?.email?.split("@")?.[0]?.replace(/\./g, " ") ||
      "Jenny Wilson",
  );
  const displayName =
    captalizeEachWord(
      [profile?.firstName, profile?.lastName].filter(Boolean).join(" "),
    ) || fallbackName;
  const emailAddress = profile?.email || currentUser?.email || "jennywilson@gmail.com";
  const avatarUrl = addBaseUrl(profile?.profileUrl ?? undefined);
  const initials = displayName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  useEffect(() => {
    setNotificationsEnabled(
      profile?.userSettings?.isPushNotificationEnabled ??
        currentUser?.userSettings?.isPushNotificationEnabled ??
        true,
    );
  }, [
    currentUser?.userSettings?.isPushNotificationEnabled,
    profile?.userSettings?.isPushNotificationEnabled,
  ]);

  const handleNotificationToggle = async () => {
    if (isUpdatingNotification) return;
    const newValue = !notificationsEnabled;
    setNotificationsEnabled(newValue);
    setIsUpdatingNotification(true);
    try {
      await updateUserProfile({
        userSettings: { isPushNotificationEnabled: newValue },
      });
    } catch {
      setNotificationsEnabled(!newValue);
    } finally {
      setIsUpdatingNotification(false);
    }
  };

  const handleLogout = () => {
    clearOnLogout();
    dispatch(clear());
    navigate(AUTH_ROUTE_PATHS.getStarted);
  };

  const handleDeleteAccount = async () => {
    const userId = profile?.id ?? getCurrentUser("id");

    if (!userId) {
      toast.error("Unable to delete account. User ID not found.");
      return;
    }

    try {
      await toast.promise(deleteUserAccount(userId), {
        loading: "Deleting account...",
        success: "Account deleted successfully",
        error: (error) =>
          error?.response?.data?.message ||
          error?.message ||
          "Failed to delete account",
      });

      clearOnLogout();
      dispatch(clear());
      navigate(AUTH_ROUTE_PATHS.signIn);
    } catch {
      return;
    }
  };

  if (isLoading && !profile && !error) {
    return (
      <section className={styles.page}>
        <div className={styles.orbLeft} />
        <div className={styles.orbRight} />
        <div className={styles.dotsRight} />

        <div className={styles.inner}>
          <Loader fullscreen={false} size={96} />
        </div>
      </section>
    );
  }

  return (
    <section className={styles.page}>
      <div className={styles.orbLeft} />
      <div className={styles.orbRight} />
      <div className={styles.dotsRight} />

      <div className={styles.inner}>
        <div className={styles.profileCard}>
          <div className={styles.profileHeader}>
            <div className={styles.avatarWrap}>
              <div className={styles.avatarFrame}>
                {avatarUrl ? (
                  <img
                    alt={displayName}
                    className={styles.avatarImage}
                    src={avatarUrl}
                  />
                ) : (
                  <div className={styles.avatarFallback}>{initials || "JW"}</div>
                )}
              </div>

              <button
                aria-label={t(["profile.edit.title", "Edit profile"], "Edit profile")}
                className={styles.editBadge}
                onClick={() => setIsEditModalOpen(true)}
                type="button"
              >
                <PencilMiniIcon />
              </button>
            </div> 

            <div className={styles.profileInfo}>
              <h1>{displayName}</h1>
              <p>{emailAddress}</p>
              {error ? (
                <span className={`${styles.statusText} ${styles.statusError}`}>
                  {error}
                </span>
              ) : null}
            </div>
          </div>

          <div className={styles.settingsList}>
            <div className={styles.settingsRow}>
              <div className={styles.rowLeft}>
                <span className={styles.rowIcon}>
                  <NotificationMiniIcon />
                </span>
                <span className={styles.rowLabel}>
                  {t(
                    ["profile.notifications.label", "Notifications"],
                    "Notifications",
                  )}
                </span>
              </div>

              <button
                aria-label={
                  notificationsEnabled
                    ? t(
                        [
                          "profile.notifications.disable",
                          "Disable notifications",
                        ],
                        "Disable notifications",
                      )
                    : t(
                        [
                          "profile.notifications.enable",
                          "Enable notifications",
                        ],
                        "Enable notifications",
                      )
                }
                className={`${styles.switch} ${
                  notificationsEnabled ? styles.switchActive : ""
                }`}
                disabled={isUpdatingNotification}
                onClick={handleNotificationToggle}
                type="button"
              >
                <span className={styles.switchThumb} />
              </button>
            </div>

            {PROFILE_LINKS.map(({ icon: Icon, labelKey, fallbackLabel, to }) => {
              const label = t([labelKey, fallbackLabel], fallbackLabel);

              return (
                <Link className={styles.settingsLink} key={labelKey} to={to}>
                  <div className={styles.rowLeft}>
                    <span className={styles.rowIcon}>
                      <Icon />
                    </span>
                    <span className={styles.rowLabel}>{label}</span>
                  </div>

                  <ChevronRightMiniIcon className={styles.rowChevron} />
                </Link>
              );
            })}
          </div>
        </div>

        <div className={styles.actionFooter}>
          <button
            className={styles.deleteAccountButton}
            onClick={() => setConfirmModal("deleteAccount")}
            type="button"
          >
            <span>
              {t(
                ["profile.actions.deleteAccount", "Delete account"],
                "Delete account",
              )}
            </span>
          </button>

          <button
            className={styles.logoutButton}
            onClick={() => setConfirmModal("logout")}
            type="button"
          >
            <span>{t(["profile.actions.logout", "Log out"], "Log out")}</span>
          </button>
        </div>
      </div>

      <LogoutConfirmModal
        closeModal={(open) => {
          if (!open) {
            setConfirmModal(null);
          }
        }}
        confirmLabel={
          confirmModal === "deleteAccount"
            ? t(
                ["profile.actions.deleteAccount", "Delete account"],
                "Delete account",
              )
            : t(["profile.actions.logout", "Log out"], "Log out")
        }
        heading={
          confirmModal === "deleteAccount"
            ? t(
                ["profile.deleteAccount.title", "Delete account"],
                "Delete account",
              )
            : t(["profile.logout.title", "Log out"], "Log out")
        }
        message={
          confirmModal === "deleteAccount"
            ? t(
                [
                  "profile.deleteAccount.message",
                  "Are you sure you want to delete your account? This action cannot be undone.",
                ],
                "Are you sure you want to delete your account? This action cannot be undone.",
              )
            : t(
                [
                  "profile.logout.message",
                  "Are you sure you want to log out?",
                ],
                "Are you sure you want to log out?",
              )
        }
        modalOpen={confirmModal !== null}
        onConfirm={
          confirmModal === "deleteAccount" ? handleDeleteAccount : handleLogout
        }
        variant={confirmModal ?? "logout"}
      />

      <EditProfileModal
        notificationsEnabled={notificationsEnabled}
        onClose={() => setIsEditModalOpen(false)}
        onUpdated={async () => {
          try {
            await dispatch(fetchUserProfile()).unwrap();
          } catch {
            return;
          }
        }}
        open={isEditModalOpen}
        profile={profile}
      />
    </section>
  );
};

export default Profile;
